import { t } from "../i18n";

/**
 * Screen point where the radial was opened (the raw background-tap location).
 * Generation/node placement uses this anchor, never the clamped display center,
 * so an entity always spawns where the user tapped even when the menu itself was
 * pulled inward to fit on screen.
 */
export interface RadialAnchor {
  clientX: number;
  clientY: number;
}

/**
 * One radial item. A branch (`children`) fans out a sub-level on confirm; a leaf
 * (`onSelect`) runs its action and closes. `children` is a thunk so feature
 * gating / room state is re-evaluated at open time, never cached stale.
 */
export interface RadialItem {
  key: string;
  label: string;
  iconSvg?: string;
  disabled?: boolean;
  children?: () => RadialItem[];
  onSelect?: (anchor: RadialAnchor) => void;
}

export interface RadialMenuApi {
  open: (clientX: number, clientY: number, adoptPointerId?: number) => void;
  close: () => void;
  isOpen: () => boolean;
  setRoot: (items: RadialItem[]) => void;
  /** Bounding rect of the ring item whose key matches, or null when not shown. */
  itemRect: (key: string) => DOMRect | null;
  /** Bounding rect of the Back item, or null when at root / closed. */
  backRect: () => DOMRect | null;
  /** Keys of the items currently rendered on the active level (excludes Back). */
  visibleKeys: () => string[];
  /** Drill depth: 0 at root. */
  depth: () => number;
  /** Confirms the active-level item whose key matches, exactly like a tap on its wedge; absent keys are ignored. */
  selectItem: (key: string) => void;
  /** Confirms the Back item (one level up), exactly like a tap on its wedge; ignored at root / closed. */
  selectBack: () => void;
}

interface RadialDeps {
  suspendControls: () => void;
  resumeControls: () => void;
  overlayClass?: string;
  omitRootCancel?: boolean;
  keepOpenOnSelect?: boolean;
  itemsDisabled?: () => boolean;
  limitProgress?: () => number | null;
}

const INNER_R = 88;
const OUTER_R = 177;
const DEADZONE_R = INNER_R;
const SELECT_MAX = OUTER_R + 34;
const CONTENT_R = (INNER_R + OUTER_R) / 2;
const GAP_PX = 7;
const BOX = 2 * OUTER_R;
const VIEWPORT_MARGIN = 12;
const BACK_KEY = "__back__";
const CANCEL_KEY = "__cancel__";
const TOP_KEYS: ReadonlySet<string> = new Set([BACK_KEY, CANCEL_KEY]);

function deg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * clip-path polygon for one annular sector inside a BOX² box centered on the
 * ring origin. The two straight sides are offset perpendicular by GAP_PX/2 from
 * the boundary radials (thLo, thHi in continuous degrees) so neighbouring wedges
 * leave a constant-width — i.e. parallel-edged — gap instead of a V that pinches
 * to nothing at the hub. Inner/outer arcs are sampled between the offset corners at a
 * fixed angular density (so a few-item menu's wide wedges stay smooth rather than
 * faceting like a low-poly arc), with a floor for the many-item case.
 */
function wedgeClip(thLo: number, thHi: number): string {
  const half = GAP_PX / 2;
  const rOut = OUTER_R - 1;
  const dIn = deg(Math.asin(half / INNER_R));
  const dOut = deg(Math.asin(half / rOut));
  const pct = (r: number, t: number): string => {
    const rad = (t * Math.PI) / 180;
    const x = ((OUTER_R + r * Math.cos(rad)) / BOX) * 100;
    const y = ((OUTER_R + r * Math.sin(rad)) / BOX) * 100;
    return `${x.toFixed(2)}% ${y.toFixed(2)}%`;
  };
  const pts: string[] = [pct(rOut, thLo + dOut), pct(INNER_R, thLo + dIn)];
  const steps = Math.max(8, Math.ceil((thHi - thLo) / 6));
  for (let i = 1; i < steps; i++) {
    pts.push(pct(INNER_R, thLo + dIn + ((thHi - dIn - (thLo + dIn)) * i) / steps));
  }
  pts.push(pct(INNER_R, thHi - dIn), pct(rOut, thHi - dOut));
  for (let i = 1; i < steps; i++) {
    pts.push(pct(rOut, thHi - dOut + ((thLo + dOut - (thHi - dOut)) * i) / steps));
  }
  return `polygon(${pts.join(", ")})`;
}

export function createRadialMenu(deps: RadialDeps): RadialMenuApi {
  const overlay = document.createElement("div");
  overlay.className = "radial-overlay";
  if (deps.overlayClass !== undefined) {
    overlay.classList.add(deps.overlayClass);
  }
  overlay.style.display = "none";

  const ring = document.createElement("div");
  ring.className = "radial-ring";
  overlay.appendChild(ring);

  const centerDot = document.createElement("div");
  centerDot.className = "radial-center";
  ring.appendChild(centerDot);

  const limitRing = document.createElement("div");
  limitRing.className = "radial-limit-progress";
  limitRing.style.display = "none";
  limitRing.style.width = `${2 * INNER_R}px`;
  limitRing.style.height = `${2 * INNER_R}px`;
  limitRing.style.margin = `${-INNER_R}px 0 0 ${-INNER_R}px`;
  ring.appendChild(limitRing);

  document.body.appendChild(overlay);

  let rootItems: RadialItem[] = [];
  let navStack: string[] = [];
  let level: RadialItem[] = [];
  let anchor: RadialAnchor = { clientX: 0, clientY: 0 };
  let center = { x: 0, y: 0 };
  let open = false;
  let strokePointerId: number | null = null;
  let enteredSector: number | null = null;
  let progressRaf: number | null = null;
  let wasLimited = false;
  let strokeAdopted = false;
  const sectorEls = new Map<string, { wedge: HTMLElement; content: HTMLElement }>();

  function resolvePath(): RadialItem[] {
    let items = rootItems;
    for (const key of navStack) {
      const branch = items.find((it: RadialItem): boolean => it.key === key);
      if (branch === undefined || branch.children === undefined) {
        navStack = [];
        return rootItems;
      }
      items = branch.children();
    }
    return items;
  }

  function currentItems(): RadialItem[] {
    const base = resolvePath();
    if (navStack.length === 0) {
      if (deps.omitRootCancel === true) {
        return base;
      }
      return [{ key: CANCEL_KEY, label: t("radial.cancel") }, ...base];
    }
    return [{ key: BACK_KEY, label: t("radial.back") }, ...base];
  }

  /**
   * Angle (deg) of item i's center. When a Back item leads the level it sits
   * dead-centre at 12 o'clock (-90°) and the rest fan out around it; otherwise
   * items straddle 12 o'clock so a seam — not a wedge — sits under the top × .
   */
  function centerDeg(i: number, n: number, topCentered: boolean): number {
    return -90 + (i + (topCentered ? 0 : 0.5)) * (360 / n);
  }

  function topCentered(): boolean {
    return level[0] !== undefined && TOP_KEYS.has(level[0].key);
  }

  /** True when an item cannot be selected: explicitly disabled, or a non-top item while the menu is rate-limited. */
  function isItemDisabled(item: RadialItem): boolean {
    return item.disabled === true || (deps.itemsDisabled?.() === true && !TOP_KEYS.has(item.key));
  }

  function renderLevel(): void {
    level = currentItems();
    for (const rec of sectorEls.values()) {
      rec.wedge.remove();
      rec.content.remove();
    }
    sectorEls.clear();
    const n = level.length;
    const w = 360 / n;
    const topC = topCentered();
    level.forEach((item: RadialItem, i: number): void => {
      const c = centerDeg(i, n, topC);
      const wedge = document.createElement("div");
      wedge.className = "radial-wedge";
      if (TOP_KEYS.has(item.key)) {
        wedge.classList.add("radial-wedge-back");
      }
      const disabled = isItemDisabled(item);
      if (disabled) {
        wedge.classList.add("radial-wedge-disabled");
      }
      wedge.style.clipPath = wedgeClip(c - w / 2, c + w / 2);

      const content = document.createElement("div");
      content.className = disabled ? "radial-wedge-content radial-wedge-disabled" : "radial-wedge-content";
      const mid = (c * Math.PI) / 180;
      content.style.left = `${CONTENT_R * Math.cos(mid)}px`;
      content.style.top = `${CONTENT_R * Math.sin(mid)}px`;
      const icon = iconMarkup(item);
      content.innerHTML = `<span class="radial-sector-icon">${icon}</span><span class="radial-sector-label">${item.label}</span>`;

      ring.appendChild(wedge);
      ring.appendChild(content);
      sectorEls.set(item.key, { wedge, content });
    });
    highlight(null);
  }

  function iconMarkup(item: RadialItem): string {
    if (item.key === BACK_KEY) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>`;
    }
    if (item.key === CANCEL_KEY) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6 L18 18 M18 6 L6 18"/></svg>`;
    }
    const inner = item.iconSvg ?? "";
    if (inner.startsWith("<svg")) {
      return inner;
    }
    return inner !== "" ? `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">${inner}</svg>` : "";
  }

  function clampCenter(clientX: number, clientY: number): { x: number; y: number } {
    const reach = OUTER_R + 16;
    const x = Math.min(window.innerWidth - reach - VIEWPORT_MARGIN, Math.max(reach + VIEWPORT_MARGIN, clientX));
    const y = Math.min(window.innerHeight - reach - VIEWPORT_MARGIN, Math.max(reach + VIEWPORT_MARGIN, clientY));
    return { x, y };
  }

  function layout(): void {
    ring.style.left = `${center.x}px`;
    ring.style.top = `${center.y}px`;
  }

  type HitTarget = { kind: "sector"; index: number } | { kind: "center" } | { kind: "outside" };

  function hitTarget(clientX: number, clientY: number): HitTarget {
    const dx = clientX - center.x;
    const dy = clientY - center.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= DEADZONE_R) {
      return { kind: "center" };
    }
    if (dist > SELECT_MAX) {
      return { kind: "outside" };
    }
    const n = level.length;
    if (n === 0) {
      return { kind: "outside" };
    }
    const w = 360 / n;
    const off = topCentered() ? 0 : 0.5;
    const a = deg(Math.atan2(dy, dx)) + 90;
    const index = ((Math.round(a / w - off) % n) + n) % n;
    return { kind: "sector", index };
  }

  function highlight(target: HitTarget | null): void {
    sectorEls.forEach((rec: { wedge: HTMLElement; content: HTMLElement }): void => {
      rec.wedge.classList.remove("hover");
      rec.content.classList.remove("hover");
    });
    if (target === null) {
      return;
    }
    if (target.kind === "sector") {
      const item = level[target.index];
      if (item === undefined || isItemDisabled(item)) {
        return;
      }
      const rec = sectorEls.get(item.key);
      rec?.wedge.classList.add("hover");
      rec?.content.classList.add("hover");
    }
  }

  /**
   * Runs a sector's action. `forceClose` makes a keep-open menu close after firing
   * (the inward-slide gesture: fire once and dismiss) instead of staying open for
   * repeated taps (a TouchUp on the wedge itself).
   */
  function confirmSector(index: number, forceClose: boolean = false): void {
    const item = level[index];
    if (item === undefined || isItemDisabled(item)) {
      return;
    }
    if (item.key === CANCEL_KEY) {
      close();
      return;
    }
    if (item.key === BACK_KEY) {
      navStack.pop();
      enteredSector = null;
      renderLevel();
      return;
    }
    if (item.children !== undefined) {
      navStack.push(item.key);
      enteredSector = null;
      renderLevel();
      return;
    }
    const a = anchor;
    const action = item.onSelect;
    if (deps.keepOpenOnSelect === true && !forceClose) {
      enteredSector = null;
      action?.(a);
      renderLevel();
      return;
    }
    close();
    action?.(a);
  }

  function update(clientX: number, clientY: number): void {
    const target = hitTarget(clientX, clientY);
    highlight(target);
    if (target.kind === "sector") {
      enteredSector = target.index;
    } else if (target.kind === "center" && enteredSector !== null) {
      const idx = enteredSector;
      enteredSector = null;
      confirmSector(idx, true);
    }
  }

  function endStroke(): void {
    if (strokePointerId !== null) {
      try {
        overlay.releasePointerCapture(strokePointerId);
      } catch {
        // capture may already be gone (pointer left the window); nothing to release
      }
      strokePointerId = null;
    }
  }

  function stopProgress(): void {
    if (progressRaf !== null) {
      cancelAnimationFrame(progressRaf);
      progressRaf = null;
    }
    limitRing.style.display = "none";
    wasLimited = false;
  }

  /**
   * While the menu is open, drives the center progress ring from the limit signal:
   * shows the ring (0→1 fill) while rate-limited and, on the limited→free
   * transition, re-renders the level so its wedges re-enable without a reopen.
   */
  function tickProgress(): void {
    if (!open) {
      stopProgress();
      return;
    }
    const p = deps.limitProgress?.() ?? null;
    if (p === null) {
      if (wasLimited) {
        wasLimited = false;
        limitRing.style.display = "none";
        renderLevel();
      }
    } else {
      wasLimited = true;
      limitRing.style.display = "";
      limitRing.style.setProperty("--p", String(p));
    }
    progressRaf = requestAnimationFrame(tickProgress);
  }

  function doOpen(clientX: number, clientY: number, adoptPointerId?: number): void {
    anchor = { clientX, clientY };
    center = clampCenter(clientX, clientY);
    open = true;
    enteredSector = null;
    overlay.style.display = "";
    layout();
    renderLevel();
    deps.suspendControls();
    if (adoptPointerId !== undefined) {
      strokePointerId = adoptPointerId;
      strokeAdopted = true;
      try {
        overlay.setPointerCapture(adoptPointerId);
      } catch {
        // the pointer may already be gone; the stroke then simply starts on the next press
      }
    }
    if (deps.limitProgress !== undefined) {
      tickProgress();
    }
  }

  function close(): void {
    if (!open) {
      return;
    }
    open = false;
    endStroke();
    stopProgress();
    overlay.style.display = "none";
    enteredSector = null;
    strokeAdopted = false;
    deps.resumeControls();
  }

  overlay.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (!open) {
      return;
    }
    ev.preventDefault();
    strokePointerId = ev.pointerId;
    strokeAdopted = false;
    try {
      overlay.setPointerCapture(ev.pointerId);
    } catch {
      // a stale / synthetic pointer id can't be captured; the stroke still
      // tracks via the overlay's own move/up listeners
    }
    enteredSector = null;
    update(ev.clientX, ev.clientY);
  });

  overlay.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (!open || ev.pointerId !== strokePointerId) {
      return;
    }
    update(ev.clientX, ev.clientY);
  });

  overlay.addEventListener("pointerup", (ev: PointerEvent): void => {
    if (!open || ev.pointerId !== strokePointerId) {
      return;
    }
    endStroke();
    const wasAdopted = strokeAdopted;
    strokeAdopted = false;
    const target = hitTarget(ev.clientX, ev.clientY);
    if (target.kind === "outside") {
      close();
      return;
    }
    if (target.kind === "sector") {
      confirmSector(target.index);
      return;
    }
    if (enteredSector !== null) {
      const idx = enteredSector;
      enteredSector = null;
      confirmSector(idx, true);
      return;
    }
    if (deps.keepOpenOnSelect !== true || !wasAdopted) {
      close();
    }
  });

  overlay.addEventListener("pointercancel", (ev: PointerEvent): void => {
    if (ev.pointerId === strokePointerId) {
      close();
    }
  });

  function rectOfKey(key: string): DOMRect | null {
    if (!open) {
      return null;
    }
    const rec = sectorEls.get(key);
    if (rec === undefined) {
      return null;
    }
    const rect = rec.content.getBoundingClientRect();
    return rect.width === 0 && rect.height === 0 ? null : rect;
  }

  function selectByKey(key: string): void {
    if (!open) {
      return;
    }
    const index = level.findIndex((it: RadialItem): boolean => it.key === key);
    if (index !== -1) {
      confirmSector(index);
    }
  }

  return {
    open: doOpen,
    close,
    isOpen: (): boolean => open,
    setRoot: (items: RadialItem[]): void => {
      rootItems = items;
      navStack = [];
    },
    itemRect: rectOfKey,
    backRect: (): DOMRect | null => rectOfKey(BACK_KEY),
    visibleKeys: (): string[] => level.filter((it: RadialItem): boolean => !TOP_KEYS.has(it.key)).map((it: RadialItem): string => it.key),
    depth: (): number => navStack.length,
    selectItem: selectByKey,
    selectBack: (): void => selectByKey(BACK_KEY),
  };
}
